import { ApolloClient, gql } from 'apollo-boost';
import { DatabaseUserType, FeedbackSectionId, UserType } from './utils';
import { FeedbackStatus } from '../api';
import { FeedbackQuestion } from 'npo/pages/NPOFeedback/MakeEdits/api';
import { SuccessResponse } from 'npo/pages/ReportsBeta/NewReport/api';

const getSectionLevelCommentsQuery = gql`
  query getCommentBySectionId($reportId: Int!, $sectionId: String) {
    allNpoReportComments(
      filters: { reportId: $reportId, sectionId: $sectionId }
    ) {
      edges {
        node {
          userName
          userEmail
          userId
          commentId
          createdAt
          comment
          details
          userType
          sectionId
          reportId
        }
      }
    }
  }
`;

export interface SectionLevelCommentsResponse {
  allNpoReportComments: {
    commentId: number;
    userName: string;
    userId: number;
    userEmail: string;
    details: string | null; // JSON string
    comment: string;
    userType: DatabaseUserType;
    questionId: number;
    reportId: number;
    sectionId: FeedbackSectionId;
  }[];
}

export const getSectionLevelComments = (
  client: ApolloClient<object>,
  reportId: number,
  sectionId: FeedbackSectionId,
) => {
  return client.query({
    query: getSectionLevelCommentsQuery,
    variables: {
      reportId,
      sectionId,
    },
    fetchPolicy: 'network-only',
  });
};

const commentBySectionIdQuery = gql`
  query allNpoReportComments($reportId: Int!, $sectionId: String) {
    allNpoReportComments(
      filters: { reportId: $reportId, sectionId: $sectionId }
    ) {
      edges {
        node {
          commentId
          userName
          userEmail
          createdAt
          userId
          details
          comment
          userType
          questionId
          sectionId
          reportId
          surveyQuestionId
        }
      }
    }
  }
`;

export interface FeedbackCommentBody {
  commentId: number;
  userName: string;
  userId: number;
  userEmail: string;
  details?: string;
  comment: string;
  userType: DatabaseUserType;
  questionId?: number;
  surveyQuestionId?: number;
  questionText?: string;
  reportId: number;
  sectionId: FeedbackSectionId;
  createdAt: string;
}

export interface CommentsBySectionIdResponse {
  allNpoReportComments: FeedbackCommentBody[];
}

export const getReportComments = (
  client: ApolloClient<object>,
  reportId: number,
  sectionId: FeedbackSectionId,
) => {
  return client.query({
    query: commentBySectionIdQuery,
    variables: {
      reportId,
      // sectionId,
    },
    fetchPolicy: 'network-only',
  });
};

const getQuestionsBySectionIdQuery = gql`
  query allNpoReportQuestions($id: ID, $sectionId: String) {
    allReports(filters: { id: $id, sectionId: $sectionId }) {
      edges {
        node {
          status
          answers(filters: { sectionId: $sectionId }) {
            edges {
              node {
                dbId
                text
                date1
                date2
                questionText
                question {
                  dbId
                  text
                  section
                }
              }
            }
          }
        }
      }
    }
  }
`;

const getAnswersBySectionQuery = gql`
  query allNpoReportQuestions($reportId: Int!, $section: String) {
    allNpoReportAnswers(filters: { reportId: $reportId, section: $section }) {
      edges {
        node {
          dbId
          text
          date1
          date2
          questionText
          question {
            dbId
            text
            section
          }
        }
      }
    }
  }
`;

const getAnswersByQuestionIdQuery = gql`
  query allNpoReportQuestions($reportId: Int!, $questionId: Int!) {
    allNpoReportAnswers(
      filters: { reportId: $reportId, questionId: $questionId }
    ) {
      edges {
        node {
          dbId
          text
          date1
          date2
          questionText
          question {
            dbId
            text
            section
          }
        }
      }
    }
  }
`;

export interface Answer {
  dbId: number;
  text: string;
  date1: string | null;
  date2: string | null;
  questionType: FeedbackQuestion['questionType'];
  questionText: string | null;
  questionId: number;
  question: {
    dbId: number;
    text: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enumResponses?: any;
    section: FeedbackSectionId;
    questionType: FeedbackQuestion['questionType'];
  };
}

export interface QuestionsBySectionIdResponse {
  allReports: {
    status: FeedbackStatus;
    answers: Answer[];
  }[];
}

export interface AnswersBySectionIdResponse {
  allNpoReportAnswers: Answer[];
}

export const toUserType = (userType: DatabaseUserType): UserType => {
  switch (userType) {
    case 'admin':
      return 'ti staff';
    case 'funder':
      return 'donor';
    default:
    case 'grantee':
      return 'npo';
  }
};

export const toDatabaseUserType = (userType: UserType): DatabaseUserType => {
  switch (userType) {
    case 'ti staff':
      return 'admin';
    case 'donor':
      return 'funder';
    default:
    case 'npo':
      return 'grantee';
  }
};

export const getQuestionsBySectionId = (
  client: ApolloClient<object>,
  reportId: number,
  sectionId: FeedbackSectionId,
) => {
  return client.query<QuestionsBySectionIdResponse>({
    query: getQuestionsBySectionIdQuery,
    variables: {
      id: reportId,
      sectionId,
    },
    fetchPolicy: 'network-only',
  });
};

export const getAnswersBySectionId = (
  client: ApolloClient<object>,
  reportId: number | string,
  section?: FeedbackSectionId,
) => {
  return client.query<AnswersBySectionIdResponse>({
    query: getAnswersBySectionQuery,
    variables: { reportId, section },
    fetchPolicy: 'network-only',
  });
};

export const getAnswersByQuestionId = (
  client: ApolloClient<object>,
  reportId: number,
  questionId: number | string,
) => {
  return client.query<AnswersBySectionIdResponse>({
    query: getAnswersByQuestionIdQuery,
    variables: { reportId, questionId },
    fetchPolicy: 'network-only',
  });
};

export interface SaveResponse {
  id: number;
  success: boolean;
}

const postQuestionLevelCommentQuery = gql`
  mutation newComment(
    $reportId: Int!
    $questionId: Int
    $sectionId: String
    $comment: String
    $details: String
    $surveyQuestionId: Int
  ) {
    newComment(
      reportId: $reportId
      questionId: $questionId
      sectionId: $sectionId
      comment: $comment
      details: $details
      surveyQuestionId: $surveyQuestionId
    ) {
      id
      success
    }
  }
`;

const customQuestionsStart = '12300';
export const getCustomQuestionId = (questionId: string | number) => {
  return `${customQuestionsStart}${questionId}`;
};
export const isCustomQuestion = (questionId?: number | string) => {
  if (!questionId) {
    return false;
  }
  return questionId.toString().startsWith(customQuestionsStart);
};
export const parseCustomQuestionId = (questionId?: number | string) => {
  if (!questionId) {
    return 0;
  }
  return (
    parseInt(questionId.toString().slice(customQuestionsStart.length), 10) || 0
  );
};
export const makeCustomQuestionId = (questionId: number | string) => {
  return `${customQuestionsStart}${questionId}`;
};

export const postQuestionLevelComment = (
  client: ApolloClient<object>,
  reportId: number | string,
  questionId: number | string,
  comment: string,
  sectionId: FeedbackSectionId,
  details?: string,
) => {
  const stringQuestionId = questionId.toString();
  const surveyQuestionId = isCustomQuestion(stringQuestionId)
    ? stringQuestionId.replace(customQuestionsStart, '')
    : undefined;

  return client.mutate<SuccessResponse<'newComment'>>({
    mutation: postQuestionLevelCommentQuery,
    variables: {
      reportId,
      questionId: surveyQuestionId ? undefined : questionId,
      surveyQuestionId: surveyQuestionId ? Number(surveyQuestionId) : undefined,
      comment,
      details,
      // sectionId,
    },
    fetchPolicy: 'no-cache',
  });
};

const updateQuestionLevelCommentQuery = gql`
  mutation updateComment($id: Int!, $answerId: Int, $comment: String) {
    updateComment(id: $id, answerId: $answerId, comment: $comment) {
      id
      success
    }
  }
`;

export const updateQuestionLevelComment = (
  client: ApolloClient<object>,
  commentId: number,
  comment: string,
  reportId: number | string,
  sectionId: FeedbackSectionId,
) => {
  return client.mutate<SuccessResponse<'updateComment'>>({
    mutation: updateQuestionLevelCommentQuery,
    variables: {
      id: commentId,
      comment,
    },
    fetchPolicy: 'no-cache',
  });
};

const updateSectionLevelCommentQuery = gql`
  mutation newComment(
    $commentId: Int
    $reportId: Int!
    $sectionId: String!
    $comment: String!
  ) {
    newComment(
      id: $commentId
      reportId: $reportId
      sectionId: $sectionId
      comment: $comment
    ) {
      success
      id
    }
  }
`;

export const updateSectionLevelComment = (
  client: ApolloClient<object>,
  commentId: number,
  comment: string,
  reportId: number,
  sectionId: FeedbackSectionId,
) => {
  return client.mutate<SuccessResponse<'updateComment'>>({
    mutation: updateSectionLevelCommentQuery,
    variables: {
      commentId,
      reportId,
      sectionId,
      comment,
    },
    fetchPolicy: 'no-cache',
  });
};

const postSectionLevelCommentQuery = gql`
  mutation newComment(
    $commentId: Int
    $reportId: Int!
    $sectionId: String!
    $comment: String!
  ) {
    newComment(
      id: $commentId
      reportId: $reportId
      sectionId: $sectionId
      comment: $comment
    ) {
      id
      success
    }
  }
`;

export const postSectionLevelComment = (
  client: ApolloClient<object>,
  commentId: number | undefined,
  comment: string,
  reportId: number,
  sectionId: FeedbackSectionId,
) => {
  return client.mutate<SuccessResponse<'newComment'>>({
    mutation: postSectionLevelCommentQuery,
    variables: {
      reportId,
      sectionId,
      comment,
    },
    fetchPolicy: 'no-cache',
  });
};

const deleteSectionLevelCommentQuery = gql`
  mutation deleteComment($id: Int!) {
    deleteComment(id: $id) {
      success
    }
  }
`;

export const deleteSectionLevelComment = (
  client: ApolloClient<object>,
  commentId: number,
  comment: string,
  reportId: number,
  sectionId: FeedbackSectionId,
) => {
  return client.mutate<SuccessResponse<'deleteComment'>>({
    mutation: deleteSectionLevelCommentQuery,
    variables: {
      id: commentId,
    },
    fetchPolicy: 'no-cache',
  });
};

const deleteQuestionLevelCommentQuery = gql`
  mutation deleteComment($id: Int!) {
    deleteComment(id: $id) {
      success
    }
  }
`;

export const deleteQuestionLevelComment = (
  client: ApolloClient<object>,
  commentId: number,
  comment: string,
  reportId: number | string,
  sectionId: FeedbackSectionId,
) => {
  return client.mutate<SuccessResponse<'deleteComment'>>({
    mutation: deleteQuestionLevelCommentQuery,
    variables: {
      id: commentId,
    },
    fetchPolicy: 'no-cache',
  });
};
